import React, { useState, useEffect } from 'react';
import './App.css';
import { sendMessageToMistral, simulateApiResponse } from './mistralApi';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeHighlight from 'rehype-highlight';
import remarkGfm from 'remark-gfm';
import 'highlight.js/styles/atom-one-dark.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faNewspaper, 
  faFileAlt, 
  faGraduationCap, 
  faArrowLeft,
  faPaperPlane,
  faHeart,
  faSearch
} from '@fortawesome/free-solid-svg-icons';

function App() {
  const [mode, setMode] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [isApiAvailable, setIsApiAvailable] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  // Check API availability when the component mounts
  useEffect(() => {
    const checkApiAvailability = async () => {
      if (!mode) return;
      
      try {
        const testMessage = "Hello";
        console.log(`Testing API availability for ${mode} mode...`);
        
        // Try to send a test message
        await sendMessageToMistral(testMessage, mode, []);
        console.log("API check successful!");
        setIsApiAvailable(true);
        setApiError(null);
      } catch (error) {
        console.warn("API check failed:", error);
        setIsApiAvailable(false);
        setApiError('Using simulated AI responses. Firebase Functions having connectivity issues.');
      }
    };
    
    checkApiAvailability();
  }, [mode]);

  const handleModeSelect = (selectedMode) => {
    setIsTransitioning(true);
    
    // Add a longer delay for smoother animation
    setTimeout(() => {
      setMode(selectedMode);
      setMessages([
        {
          role: 'system',
          content: `You are now in ${selectedMode} mode. How can I help you today?`
        }
      ]);
      
      // Automatically send specific messages based on the selected mode
      // but don't display these initial messages in the chat interface
      setTimeout(() => {
        // Create a local message array to use for the API call
        const updatedMessages = [
          {
            role: 'system',
            content: `You are now in ${selectedMode} mode. How can I help you today?`
          }
        ];
        
        // Different automatic messages for each mode
        let autoMessage = "";
        let systemResponse = "";
        
        switch(selectedMode) {
          case 'News':
            autoMessage = "Give me a news report";
            systemResponse = "Here's the latest government news:";
            break;
          case 'Summarize':
            autoMessage = "Summarize the Freedom of information act";
            systemResponse = "Preparing a summary of the Freedom of Information Act:";
            break;
          case 'Research':
            autoMessage = "Show me government spending data";
            systemResponse = "Researching government spending data:";
            break;
          case 'Education':
            autoMessage = "Teach me something";
            systemResponse = "Let's learn about government! Preparing lesson:";
            break;
          default:
            return; // No auto message for unknown modes
        }
        
        // Update system message to indicate what's coming
        setMessages([
          {
            role: 'system',
            content: systemResponse
          }
        ]);
        
        // Send the automatic message
        sendAutoModeMessage(updatedMessages, selectedMode, autoMessage);
      }, 1500); // Longer delay to ensure mode is fully set
      
      // Delay removing the transition class until after the overlay is almost gone
      setTimeout(() => {
        setIsTransitioning(false);
      }, 400); // This creates a smoother overall animation
    }, 200);
  };

  // Generalized function for handling automatic messages for any mode
  const sendAutoModeMessage = async (currentMessages, selectedMode, message) => {
    setIsLoading(true);
    
    try {
      let response;
      
      // Try the real API first, unless we already know it's not available
      if (isApiAvailable) {
        try {
          console.log(`Automatically sending ${selectedMode} message to API:`, message);
          response = await sendMessageToMistral(message, selectedMode, currentMessages);
          setIsApiAvailable(true);
          setApiError(null);
        } catch (apiError) {
          console.error("Real API call failed:", apiError);
          setIsApiAvailable(false);
          setApiError('Using simulated AI responses. Firebase Function encountered an error.');
          
          // Fallback to simulation
          console.log("Falling back to simulation");
          response = await simulateApiResponse(message, selectedMode, currentMessages);
        }
      } else {
        // Use simulation directly since we know the API is having issues
        console.log("Using simulation due to known API issues");
        response = await simulateApiResponse(message, selectedMode, currentMessages);
      }
      
      // Directly append only the assistant's response to the messages
      setMessages(prevMessages => [...prevMessages, response]);
    } catch (error) {
      console.error(`Error in sendAutoModeMessage for ${selectedMode}:`, error);
      setApiError('Unable to get a response. Please try again later.');
      setMessages(prevMessages => [
        ...prevMessages,
        { 
          role: 'system', 
          content: 'Sorry, there was an error processing your request. Please try again later.' 
        }
      ]);
    } finally {
      setIsLoading(false);
      
      // Scroll to the bottom of the chat window
      setTimeout(() => {
        const chatMessages = document.querySelector('.chat-messages');
        if (chatMessages) {
          chatMessages.scrollTop = chatMessages.scrollHeight;
        }
      }, 100);
    }
  };

  // New function to send a message without displaying it in the chat
  const sendAutoMessage = async (message) => {
    if (!mode) return;
    
    setIsLoading(true);
    
    try {
      let response;
      
      // Try the real API first, unless we already know it's not available
      if (isApiAvailable) {
        try {
          console.log("Automatically sending message to API:", message);
          response = await sendMessageToMistral(message, mode, messages);
          setIsApiAvailable(true);
          setApiError(null);
        } catch (apiError) {
          console.error("Real API call failed:", apiError);
          setIsApiAvailable(false);
          setApiError('Using simulated AI responses. Firebase Function encountered an error.');
          
          // Fallback to simulation
          console.log("Falling back to simulation");
          response = await simulateApiResponse(message, mode, messages);
        }
      } else {
        // Use simulation directly since we know the API is having issues
        console.log("Using simulation due to known API issues");
        response = await simulateApiResponse(message, mode, messages);
      }
      
      setMessages(prevMessages => [...prevMessages, response]);
    } catch (error) {
      console.error('Error in sendAutoMessage:', error);
      setApiError('Unable to get a response. Please try again later.');
      setMessages(prevMessages => [
        ...prevMessages,
        { 
          role: 'system', 
          content: 'Sorry, there was an error processing your request. Please try again later.' 
        }
      ]);
    } finally {
      setIsLoading(false);
      
      // Scroll to the bottom of the chat window
      setTimeout(() => {
        const chatMessages = document.querySelector('.chat-messages');
        if (chatMessages) {
          chatMessages.scrollTop = chatMessages.scrollHeight;
        }
      }, 100);
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim() || !mode) return;
    
    const userMessage = { role: 'user', content: input };
    setMessages([...messages, userMessage]);
    setInput('');
    setIsLoading(true);
    
    try {
      let response;
      
      // Try the real API first, unless we already know it's not available
      if (isApiAvailable) {
        try {
          console.log("Attempting to call real API endpoint...");
          response = await sendMessageToMistral(input, mode, messages);
          setIsApiAvailable(true);
          setApiError(null);
        } catch (apiError) {
          console.error("Real API call failed:", apiError);
          setIsApiAvailable(false);
          setApiError('Using simulated AI responses. Firebase Function encountered an error.');
          
          // Fallback to simulation
          console.log("Falling back to simulation");
          response = await simulateApiResponse(input, mode, messages);
        }
      } else {
        // Use simulation directly since we know the API is having issues
        console.log("Using simulation due to known API issues");
        response = await simulateApiResponse(input, mode, messages);
      }
      
      setMessages(prevMessages => [...prevMessages, response]);
    } catch (error) {
      console.error('Error in handleSendMessage:', error);
      setApiError('Unable to get a response. Please try again later.');
      setMessages(prevMessages => [
        ...prevMessages,
        { 
          role: 'system', 
          content: 'Sorry, there was an error processing your request. Please try again later.' 
        }
      ]);
    } finally {
      setIsLoading(false);
      
      // Scroll to the bottom of the chat window
      setTimeout(() => {
        const chatMessages = document.querySelector('.chat-messages');
        if (chatMessages) {
          chatMessages.scrollTop = chatMessages.scrollHeight;
        }
      }, 100);
    }
  };

  const handleBackToHome = () => {
    setIsTransitioning(true);
    
    // Add a longer delay for smoother animation
    setTimeout(() => {
      setMode(null);
      
      // Delay removing the transition class until after the overlay is almost gone
      setTimeout(() => {
        setIsTransitioning(false);
      }, 400); // This creates a smoother overall animation
    }, 200);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Helper function to get avatar initials
  const getInitials = (role) => {
    switch(role) {
      case 'user':
        return 'U';
      case 'assistant':
        return 'AI';
      case 'system':
        return 'S';
      default:
        return '?';
    }
  };

  const renderMessageContent = (message) => {
    // Only use markdown for assistant and system messages
    if (message.role === 'assistant' || message.role === 'system') {
      return (
        <ReactMarkdown
          children={message.content}
          rehypePlugins={[rehypeRaw, rehypeHighlight]}
          remarkPlugins={[remarkGfm]}
          className="markdown-content"
          components={{
            // Make links open in new tab
            a: ({node, ...props}) => (
              <a {...props} target="_blank" rel="noopener noreferrer" />
            ),
            // Additional styling for code blocks
            code: ({node, inline, className, children, ...props}) => {
              const match = /language-(\w+)/.exec(className || '');
              return !inline ? (
                <div className="code-block-wrapper">
                  <div className="code-block-header">
                    <span>{match ? match[1] : 'code'}</span>
                  </div>
                  <code className={className} {...props}>
                    {children}
                  </code>
                </div>
              ) : (
                <code className={className} {...props}>
                  {children}
                </code>
              );
            }
          }}
        />
      );
    }
    // For user messages, just return the plain text
    return <div>{message.content}</div>;
  };

  return (
    <div className={`App ${isTransitioning ? 'transitioning' : ''}`}>
      {!mode ? (
        <div className="home-screen">
          <h1>Welcome to <span>Gov Glance AI</span></h1>
          <p className="intro-text">
            AI is here to stay. It allows us to accomplish amazing things but also can be very misleading if used incorrectly. 
            We are dedicated to AI research that makes sure AI is <strong>working for us, not against us</strong>. 
            Improving our ability to understand and write legislation, research government spending, and enhance our education practices.
          </p>
          <div className="mode-buttons">
            <button onClick={() => handleModeSelect('News')} className="mode-button news">
              <FontAwesomeIcon icon={faNewspaper} className="button-icon" /> News
            </button>
            <button onClick={() => handleModeSelect('Summarize')} className="mode-button understand">
              <FontAwesomeIcon icon={faFileAlt} className="button-icon" /> Summarize
            </button>
            <button onClick={() => handleModeSelect('Research')} className="mode-button research">
              <FontAwesomeIcon icon={faSearch} className="button-icon" /> Research
            </button>
            <button onClick={() => handleModeSelect('Education')} className="mode-button education">
              <FontAwesomeIcon icon={faGraduationCap} className="button-icon" /> Education
            </button>
          </div>
          
          <div className="donation-section">
            <p>As a non-profit the Gov Glance Foundation relies on your support.</p>
            <a href="https://donate.stripe.com/28o3fXbdvbQO2RO288" target="_blank" rel="noopener noreferrer" className="donate-button">
              <FontAwesomeIcon icon={faHeart} className="heart-icon" /> Donate
            </a>
          </div>
        </div>
      ) : (
        <div className={`chat-interface ${mode.toLowerCase()}`}>
          <div className="chat-header">
            <button onClick={handleBackToHome} className="back-button">
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Home
            </button>
            <h2>Gov Glance AI - <span>{mode}</span> Mode</h2>
          </div>
          {apiError && (
            <div className="api-error-banner">
              {apiError}
            </div>
          )}
          <div className="chat-messages">
            {messages.map((message, index) => (
              <div key={index} className={`message ${message.role}`}>
                <div className="message-avatar">{getInitials(message.role)}</div>
                <div className="message-bubble">
                  <div className="message-content">
                    {renderMessageContent(message)}
                  </div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="message assistant loading">
                <div className="message-avatar">AI</div>
                <div className="message-bubble">
                  <div className="message-content">Thinking</div>
                </div>
              </div>
            )}
          </div>
          <div className="chat-input">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={`Ask something about ${mode.toLowerCase()}...`}
            />
            <button onClick={handleSendMessage} disabled={isLoading || !input.trim()}>
              <FontAwesomeIcon icon={faPaperPlane} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
